import { getRandomId as getRandomNodeId, ROOT_NODE } from "@craftjs/utils";
import { blocksLibrary } from "../blocks";
import { waitForElement, waitForLastElement } from "../dom";
import {
  isJsonString,
  removeCharsetMetaTag,
  removeWindowsFragments,
} from "../helpers";
import { rawPasteFilesHandler, rawPasteHandler } from "../raw-parser";
import { addNodeTree } from "../editor/component-blocks";
import { setEndOfContenteditable } from "../helpers/richtext/caret-position";

function getBlocks(actions, query, id, data = {}, document = window.document) {
  if (!query || !actions) {
    return;
  }

  id = id ?? query.getEvent("selected").first() ?? ROOT_NODE;

  // Breakdown data and check if it has grigora/blocks key
  var { html = "", plainText = "", files = "", grigoraBlockJson } = data;

  // Remove Windows-specific metadata appended within copied HTML text.
  html = removeWindowsFragments(html);

  // Strip meta tag.
  html = removeCharsetMetaTag(html);

  // If copied data has grigora blocks
  if (grigoraBlockJson) {
    return {
      blocks: grigoraBlockJson,
      nodeTreesFormat: true,
    };
  }

  // Process any attached files, unless we infer that the files in
  // question are redundant "screenshots" of the actual HTML payload,
  // as created by certain office-type programs.
  if (files?.length && !shouldDismissPastedFiles(files, html, plainText)) {
    const content = rawPasteFilesHandler(files);
    return {
      blocks: content,
    };
  }

  var content = rawPasteHandler(html, plainText, "p");

  var selectedBlocks = query.getEvent("selected").all() ?? [];

  if (typeof content === "string" && selectedBlocks.length > 1) {
    content = [
      {
        blockSlug: "grigora/text",
        blockProps: {
          content: content,
        },
      },
    ];
  }

  return {
    blocks: content,
    nodeTreesFormat: false,
    forceDeleteSelected: selectedBlocks.length > 1,
  };
}

function addNodeSingleTree(actions, query, id, index = 0, tree, offset = 0) {
  if (!tree) {
    return;
  }

  if (!id) {
    return;
  }

  return addNodeTree(tree, actions, query, id, index + offset + 1);
}

function addBlockInCraftJS(
  actions,
  query,
  Component,
  blockProps = {},
  parent,
  index,
  inPlace = false,
  canvas = false,
  focusCaretOnEnd = false,
  offset = 0,
) {
  const nodeID = getRandomNodeId();

  const freshNode = {
    id: nodeID,
    data: {
      type: Component,
      isCanvas: canvas ? true : undefined,
      props: blockProps,
    },
  };

  const nodeToAdd = query.parseFreshNode(freshNode).toNode();

  actions.add(nodeToAdd, parent, index + offset + 1);
  if (inPlace) {
    actions.delete(id);
  }

  actions.selectNode(nodeID);

  if (focusCaretOnEnd) {
    waitForLastElement(
      document,
      `[data-grigora-block-id="${nodeID}"] .grigora-richtext-input__field`,
    ).then((elm) => {
      setEndOfContenteditable(document, elm);
    });
  }
  return nodeID;
}

function addBlock(
  actions,
  query,
  blockSlug,
  blockProps,
  parent,
  index,
  inPlace,
  focusCaretOnEnd = false,
  offset = 0,
) {
  if (blocksLibrary[blockSlug]) {
    if (blockSlug === "grigora/heading") {
      blockProps.structureTag = "h2";
      blockProps.content = "";
    }

    var newID = addBlockInCraftJS(
      actions,
      query,
      blocksLibrary[blockSlug],
      blockProps,
      parent,
      index,
      inPlace,
      blocksLibrary[blockSlug].craft.related.supportNestedBlocks,
      focusCaretOnEnd,
      offset,
    );

    return newID;
  }
}

function paste(actions, query, id, data = {}, document = window.document) {
  if (!query || !actions) {
    return;
  }

  id = id ?? query.getEvent("selected").first() ?? ROOT_NODE;

  const {
    blocks,
    nodeTreesFormat = false,
    forceDeleteSelected = false,
  } = getBlocks(actions, query, id, data, document) ?? {};

  if (!blocks) {
    return;
  }

  var target, index;

  // Check if selected node is canvas
  if (query.node(id)?.get()?.data?.isCanvas) {
    target = id;
    // set index to children length
    index = query.node(id).childNodes().length ?? 0;
  }

  // Select target among ancestors
  if (!target) {
    let parents = query.node(id).ancestors();

    // Loop through ancestors and select first canvas
    for (let i = 0; i < parents.length; i++) {
      const parent = parents[i];

      if (query.node(parent)?.get()?.data?.isCanvas) {
        target = parent;

        // If selected parent is immediate parent, set index to current index + 1
        if (i === 0) {
          let childrenstemp = query.node(parent).childNodes();
          index =
            childrenstemp.indexOf(id) > -1 ? childrenstemp.indexOf(id) : 0;
        } else {
          index = query.node(parent).childNodes().length ?? 0;
        }

        break;
      }
    }

    if (!target) {
      // If no canvas found, select first ancestor
      target = ROOT_NODE;
      index = query.node(ROOT_NODE).childNodes().length ?? 0;
    }
  }

  if (!target) {
    return;
  }

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];

    if (nodeTreesFormat) {
      addNodeSingleTree(actions, query, target, index, block, i);
    } else {
      addBlock(
        actions,
        query,
        block.blockSlug,
        block.blockProps,
        target,
        index,
        false,
        true,
        i,
      );
    }
  }
}

function pasteParsed(
  actions,
  query,
  id,
  blocks,
  document = window.document,
  focusCaretOnEnd = false,
) {
  if (!query || !actions) {
    return;
  }

  id = id ?? query.getEvent("selected").first() ?? ROOT_NODE;

  if (!blocks) {
    return;
  }

  var target, index;

  // Check if selected node is canvas
  if (query.node(id)?.get()?.data?.isCanvas) {
    target = id;
    // set index to children length
    index = query.node(id).childNodes().length ?? 0;
  }

  // Select target among ancestors
  if (!target) {
    let parents = query.node(id).ancestors();

    // Loop through ancestors and select first canvas
    for (let i = 0; i < parents.length; i++) {
      const parent = parents[i];

      if (query.node(parent)?.get()?.data?.isCanvas) {
        target = parent;

        // If selected parent is immediate parent, set index to current index + 1
        if (i === 0) {
          let childrenstemp = query.node(parent).childNodes();
          index =
            childrenstemp.indexOf(id) > -1 ? childrenstemp.indexOf(id) : 0;
        } else {
          index = query.node(parent).childNodes().length ?? 0;
        }

        break;
      }
    }

    if (!target) {
      // If no canvas found, select first ancestor
      target = ROOT_NODE;
      index = query.node(ROOT_NODE).childNodes().length ?? 0;
    }
  }

  if (!target) {
    return;
  }

  var recentBlock;

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    recentBlock = addNodeSingleTree(actions, query, target, index, block, i);
  }

  if (recentBlock && focusCaretOnEnd) {
    waitForElement(
      document,
      `[data-grigora-block-id="${recentBlock}"] .grigora-richtext-input__field`,
    ).then((elm) => {
      actions.selectNode(recentBlock);
      setEndOfContenteditable(document, elm);
    });
  }
}

function pasteNewBlocks(
  actions,
  query,
  id,
  blocks,
  document = window.document,
  focusCaretOnEnd = false,
) {
  let historyMergeAction = actions.history.merge();

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];

    addBlock(
      actions,
      query,
      block.blockSlug,
      block.blockProps,
      id,
      i - 1,
      false,
      i === blocks.length - 1,
      undefined,
      historyMergeAction,
    );
  }
}

export { paste, pasteParsed, pasteNewBlocks };
